// src/pages/Projects/ProjectList.jsx
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProjects, deleteProject, createReport } from '../services/projectService';
import { getMyTeams } from '../services/action';
import { Table, Button, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { FiPlus, FiEdit, FiTrash2, FiAlertTriangle } from 'react-icons/fi';

// Report type configurations
const REPORT_TYPES = {
  DAMAGE: {
    label: 'Damage Report',
    icon: '🚧',
    fields: [
      { name: 'title', label: 'Title', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'textarea', required: true },
      { name: 'location', label: 'Location', type: 'text', required: true },
      { name: 'severity', label: 'Severity', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'], required: true },
      { name: 'images', label: 'Images', type: 'file', multiple: true }
    ]
  },
  TASK: {
    label: 'Task Report',
    icon: '✅',
    fields: [
      { name: 'taskName', label: 'Task Name', type: 'text', required: true },
      { name: 'status', label: 'Status', type: 'select', options: ['Not Started', 'In Progress', 'Completed', 'Blocked'], required: true },
      { name: 'progress', label: 'Progress (%)', type: 'number', min: 0, max: 100 },
      { name: 'comments', label: 'Comments', type: 'textarea' }
    ]
  },
  EQUIPMENT: {
    label: 'Equipment Report',
    icon: '🔧',
    fields: [
      { name: 'equipmentId', label: 'Equipment ID', type: 'text', required: true },
      { name: 'issueType', label: 'Issue Type', type: 'select', options: ['Maintenance', 'Malfunction', 'Safety', 'Missing'], required: true },
      { name: 'description', label: 'Description', type: 'textarea', required: true }
    ]
  },
  SAFETY: {
    label: 'Safety Report',
    icon: '⚠️',
    fields: [
      { name: 'hazardType', label: 'Hazard Type', type: 'select', options: ['Chemical', 'Electrical', 'Mechanical', 'Other'], required: true },
      { name: 'location', label: 'Exact Location', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'textarea', required: true },
      { name: 'urgency', label: 'Urgency', type: 'select', options: ['Low', 'Medium', 'High', 'Immediate'], required: true }
    ]
  }
};

const ProjectList = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [userTeamIds, setUserTeamIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [reportData, setReportData] = useState({ 
    type: 'DAMAGE',
    files: []
  });
  const [reportSubmitting, setReportSubmitting] = useState(false);

  // Fetch projects and user teams
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user's teams
        const myTeamsResponse = await getMyTeams();
        const myTeamIds = myTeamsResponse.map(team => team._id);
        setUserTeamIds(myTeamIds);
        
        // Fetch all projects
        const projectsResponse = await getProjects();
        const allProjects = projectsResponse.data.data || [];
        
        // Filter projects based on user role
        const filteredProjects = user?.role === 'technician' 
          ? allProjects.filter(project => {
              const isInProjectTeam = project.teams?.some(projectTeamId => 
                myTeamIds.includes(projectTeamId.toString())
              );
              const isSupervisor = project.supervisors?.some(
                supervisorId => supervisorId.toString() === user._id
              );
              return isInProjectTeam || isSupervisor;
            })
          : allProjects;
        
        setProjects(filteredProjects);
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.response?.data?.message || 'Error loading projects');
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Handle report modal open
  const handleReportClick = (project) => {
    setCurrentProject(project);
    setReportData({ type: 'DAMAGE', files: [] });
    setError('');
    setShowReportModal(true);
  };

  // Handle form field changes
  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file uploads
  const handleFileChange = (e) => {
    setReportData(prev => ({
      ...prev,
      files: Array.from(e.target.files)
    }));
  };

  // Submit report
  const handleReportSubmit = async () => {
    // Validate required fields
    const currentReportType = REPORT_TYPES[reportData.type];
    const missingFields = currentReportType.fields
      .filter(field => field.required && !reportData[field.name])
      .map(field => field.label);

    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setReportSubmitting(true);
      
      // Prepare form data for potential file uploads
      const formData = new FormData();
      formData.append('projectId', currentProject._id);
      formData.append('type', reportData.type);
      formData.append('reportedBy', user._id);
      formData.append('reportData', JSON.stringify(reportData));
      
      // Append files if any
      if (reportData.files && reportData.files.length > 0) {
        reportData.files.forEach(file => {
          formData.append('files', file);
        });
      }

      await createReport(formData);
      
      // Reset and show success
      setShowReportModal(false);
      setError('');
      setSuccess(`${currentReportType.label} submitted successfully!`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit report');
    } finally {
      setReportSubmitting(false);
    }
  };

  // Get user's role in a specific project
  const getUserRoleInProject = (project) => {
    if (project.supervisors?.some(id => id.toString() === user._id)) {
      return 'Supervisor';
    }
    if (project.teams?.some(teamId => userTeamIds.includes(teamId.toString()))) {
      return 'Team Member';
    }
    return 'Not Assigned';
  };

  // Handle project deletion
  const handleDelete = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteProject(projectId);
        setProjects(projects.filter(project => project._id !== projectId));
        setSuccess('Project deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Delete failed');
      }
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading projects...</p>
      </div>
    );
  }

  // Render error state
  if (error && !showReportModal) {
    return (
      <Alert variant="danger" className="mt-4" onClose={() => setError('')} dismissible>
        {error}
      </Alert>
    );
  }

  return (
    <div className="container py-4">
      {/* Report Modal */}
      <Modal show={showReportModal} onHide={() => setShowReportModal(false)} size="lg" style={{ marginTop: "50px" }}>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <span className="me-2">{REPORT_TYPES[reportData.type]?.icon}</span>
            {REPORT_TYPES[reportData.type]?.label || 'Report'} - {currentProject?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Report Type*</Form.Label>
              <Form.Select
                name="type"
                value={reportData.type}
                onChange={handleReportChange}
                className="form-select-lg"
              >
                {Object.entries(REPORT_TYPES).map(([key, type]) => (
                  <option key={key} value={key}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {REPORT_TYPES[reportData.type]?.fields.map(field => {
              switch (field.type) {
                case 'textarea':
                  return (
                    <Form.Group className="mb-3" key={field.name}>
                      <Form.Label>
                        {field.label}
                        {field.required && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name={field.name}
                        value={reportData[field.name] || ''}
                        onChange={handleReportChange}
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                        required={field.required}
                      />
                    </Form.Group>
                  );
                case 'select':
                  return (
                    <Form.Group className="mb-3" key={field.name}>
                      <Form.Label>
                        {field.label}
                        {field.required && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Select
                        name={field.name}
                        value={reportData[field.name] || ''}
                        onChange={handleReportChange}
                        required={field.required}
                      >
                        <option value="">Select {field.label.toLowerCase()}</option>
                        {field.options.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  );
                case 'file':
                  return (
                    <Form.Group className="mb-3" key={field.name}>
                      <Form.Label>
                        {field.label}
                        {field.required && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Control
                        type="file"
                        multiple={field.multiple}
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                      {reportData.files?.length > 0 && (
                        <div className="mt-2">
                          <small>Selected files: {reportData.files.map(f => f.name).join(', ')}</small>
                        </div>
                      )}
                    </Form.Group>
                  );
                default:
                  return (
                    <Form.Group className="mb-3" key={field.name}>
                      <Form.Label>
                        {field.label}
                        {field.required && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Control
                        type={field.type}
                        name={field.name}
                        value={reportData[field.name] || ''}
                        onChange={handleReportChange}
                        min={field.min}
                        max={field.max}
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                        required={field.required}
                      />
                    </Form.Group>
                  );
              }
            })}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowReportModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleReportSubmit}
            disabled={reportSubmitting}
          >
            {reportSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Submitting...</span>
              </>
            ) : (
              'Submit Report'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Main Content */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Projects</h2>
        {user?.role === 'supervisor' && (
          <Button as={Link} to="/projects/new" variant="primary" className="d-flex align-items-center">
            <FiPlus className="me-2" /> New Project
          </Button>
        )}
      </div>

      {/* Success Messages */}
      {success && (
        <Alert variant="success" className="mt-3" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Projects Table */}
      {projects.length === 0 ? (
        <Alert variant="info" className="mt-4">
          {user?.role === 'technician' 
            ? "You are not assigned to any projects yet." 
            : "No projects found. Create a new project to get started."}
        </Alert>
      ) : (
        <>
          <div className="mb-3">
            <small className="text-muted">
              Showing {projects.length} {projects.length === 1 ? 'project' : 'projects'}
            </small>
          </div>
          
          <div className="table-responsive">
            <Table striped bordered hover className="align-middle">
              <thead className="table-dark">
                <tr>
                  <th>Title</th>
                  <th>Location</th>
                  <th>Dates</th>
                  <th>Status</th>
                  <th>{user?.role === 'supervisor' ? 'Team Members' : 'Your Role'}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(project => (
                  <tr key={project._id}>
                    <td>
                      <strong>{project.title}</strong>
                      {project.description && (
                        <div className="text-muted small mt-1">
                          {project.description.substring(0, 60)}...
                        </div>
                      )}
                    </td>
                    <td>
                      {project.location && (
                        <div>
                          <div className="fw-bold">
                            {project.location.city}, {project.location.country}
                          </div>
                          <small className="text-muted">
                            {project.location.address}
                          </small>
                        </div>
                      )}
                    </td>
                    <td>
                      <div>
                        <small className="text-muted">Start:</small>{' '}
                        {new Date(project.startDate).toLocaleDateString()}
                      </div>
                      {project.endDate && (
                        <div>
                          <small className="text-muted">End:</small>{' '}
                          {new Date(project.endDate).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge 
                        pill 
                        bg={
                          project.status === 'active' ? 'success' : 
                          project.status === 'on-hold' ? 'warning' : 
                          'secondary'
                        }
                        className="px-3 py-2"
                      >
                        {project.status.toUpperCase()}
                      </Badge>
                    </td>
                    <td>
                      {user?.role === 'supervisor' ? (
                        project.teams?.length > 0 ? (
                          <span className="badge bg-info">
                            {project.teams.length} team{project.teams.length !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-muted">No teams</span>
                        )
                      ) : (
                        <span className={`badge ${
                          getUserRoleInProject(project) === 'Supervisor' 
                            ? 'bg-primary' 
                            : 'bg-secondary'
                        }`}>
                          {getUserRoleInProject(project)}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        {user?.role === 'supervisor' && (
                          <>
                            <Button 
                              as={Link}
                              to={`/projects/edit/${project._id}`}
                              variant="outline-primary"
                              size="sm"
                              className="d-flex align-items-center"
                              title="Edit Project"
                            >
                              <FiEdit size={16} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(project._id)}
                              className="d-flex align-items-center"
                              title="Delete Project"
                            >
                              <FiTrash2 size={16} />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleReportClick(project)}
                          className="d-flex align-items-center"
                          title="Create Report"
                        >
                          <FiAlertTriangle size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectList;